import type { Maybe } from '@adornis/base/utilTypes.js';
import { Color } from '@adornis/chemistry/elements/theming/coloring-system.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';

// Hex equivalent of the `icsrm` daisyUI theme primary `oklch(0.7901 0.1189 195.7)`
// (see tailwind.css). Must stay in sync when the theme is changed.
export const MW_DEFAULT_PRIMARY_COLOR = '#42d2d3';
export const MW_DEFAULT_PRIMARY_CONTENT_COLOR = '#ffffff';

/**
 * Apply the (admin-configurable) matching-week primary color to both styling
 * systems in use: the daisyUI theme variables (`btn-primary`, `bg-primary`, …)
 * and the chemistry theme accent, which the login screen components
 * (`tr-button`, `tr-login-input`, …) are colored by. Without this the login
 * would keep the Buchungsheld blue while the rest of the app is teal.
 */
export const applyMatchingWeekColors = (settings?: {
  primaryColor?: Maybe<string>;
  primaryContentColor?: Maybe<string>;
}) => {
  const primary = settings?.primaryColor || MW_DEFAULT_PRIMARY_COLOR;
  const primaryContent = settings?.primaryContentColor || MW_DEFAULT_PRIMARY_CONTENT_COLOR;

  // Inline custom properties on <html> win over the theme values from tailwind.css.
  document.documentElement.style.setProperty('--color-primary', primary);
  document.documentElement.style.setProperty('--color-primary-content', primaryContent);

  const theme = DesignSystem._currentTheme.getValue();
  if (!theme) return;
  // The settings always hold `#rrggbb` values from <input type="color">.
  // By default chemistry picks black/white text from the background luminance
  // (Color.on()), which disagrees with daisyUI's --color-primary-content on
  // light colors like the teal. Force the configured content color instead.
  const contentRaw = Color.make(primaryContent as `#${string}`).raw;
  theme.colors.accent = Color.make(primary as `#${string}`, () => contentRaw);
  // Re-emit so already connected chemistry components re-render with the new accent.
  DesignSystem._currentTheme.next(theme);
};
